import ViewWork from '@Components/ViewWork'
import React, { useContext, useEffect, useState } from 'react'
import { StoresContext } from '@App/contexts'
import { ZodFormattedError } from 'zod'
import { WorkModel } from '@Entities/work/work'
import { difference } from 'lodash'
import { WorkType } from '@Components/ViewWork/work-type'
import ViewWorkHeader from '@Components/ViewWork/Headers/ViewWorkHeader'
import WizardStepHeading from '@Components/WizardFlow/WizardStepHeading'
import existingWorkSchema from '@Entities/schemas/work/existing-work-schema'
import workSchema from '@Entities/schemas/work/work-schema'
import { AlternativeTitleModel } from '@Entities/work/alternative-title-model'
import { RecordingModel } from '@Entities/work/recording-model'
import { isComposerPresent } from '@App/helpers/work-entities/fields-validators'
import { observer } from 'mobx-react'

interface ReviewWorkFormSceneProps {
    workType: WorkType
    showHeader?: boolean
}

const ReviewWorkFormScene = ({ workType, showHeader }: ReviewWorkFormSceneProps) => {
    const { workStore } = useContext(StoresContext)
    const [errors, setErrors] = useState<ZodFormattedError<WorkModel>>()

    useEffect(() => {
        const existingWorkHadComposer = workStore.workById ? isComposerPresent(workStore.workById.writers) : true
        const existingWorkHadWriters = workStore.workById ? workStore.workById.writers.length > 0 : true
        const workSchemaToUse = workStore.workDraftLocal?.isComplete
            ? existingWorkSchema(existingWorkHadComposer, existingWorkHadWriters)
            : workSchema
        const results = workSchemaToUse.safeParse(workStore.workDraftLocal)
        if (!results.success) {
            setErrors(results.error.format())
        }
        workStore.setWizardStepValidation(results.success)
    }, [])

    const isClaimWork = workType === 'claim-wizard'

    // Only show newly-added alternative titles & recordings, except on drafts
    const altTitles: AlternativeTitleModel[] =
        workType === 'draft'
            ? (workStore.workDraftLocal?.alternativeTitles ?? [])
            : difference(workStore.workDraftLocal?.alternativeTitles ?? [], workStore.workById?.alternativeTitles ?? [])
    const recordings: RecordingModel[] =
        workType === 'draft'
            ? (workStore.workDraftLocal?.recordings ?? [])
            : difference(workStore.workDraftLocal?.recordings ?? [], workStore.workById?.recordings ?? [])

    return workStore.workDraftLocal ? (
        <>
            {showHeader && <ViewWorkHeader work={workStore.workDraftLocal} errors={errors} />}
            {workType === 'create-wizard' && (
                <WizardStepHeading heading="Review" title={workStore.workDraftLocal?.title}>
                    <>
                        Please review the information you have entered for this work. If you need to edit any
                        information, click the <strong>Edit</strong> button. Once all information is correct, click the{' '}
                        <strong>Submit</strong> button.
                    </>
                </WizardStepHeading>
            )}

            {isClaimWork && (
                <WizardStepHeading heading="Review" title={workStore.workDraftLocal?.title}>
                    <>
                        Review your information before making a claim. If you would like to make any edits go back to
                        the section you would like to update. If you are satisfied with this information, press the{' '}
                        <strong>Claim</strong> button.
                    </>
                </WizardStepHeading>
            )}
            <ViewWork
                viewWork={{
                    ...workStore.workDraftLocal,
                    alternativeTitles: altTitles,
                    recordings: recordings,
                }}
                workErrors={errors}
                editing={!isClaimWork}
                workType={workType}
            />
        </>
    ) : null
}

export default observer(ReviewWorkFormScene)
